'use strict';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import * as T from 'prop-types';
import React, {Component} from 'react';
import uuidv1 from 'uuid/v1';
import {applyNumericConstraint, parseFloatEvent, parseNumberEvent} from '../../../core-utilities/react-form-utils';
import {BaseFormFieldView, FormFieldView, INPUT_TYPES, ValidationMode} from './FormFieldView';

export class FormFieldComponent extends Component {
  static propTypes = {
    className: T.string,
    type: BaseFormFieldView.propTypes.type,
    validationMode: BaseFormFieldView.propTypes.validationMode,
    label: T.string.isRequired,
    placeholder: T.string,
    value: BaseFormFieldView.propTypes.value,
    error: T.object,
    constraints: T.object,
    validateImmediately: T.bool,
    onValidate: T.func,
    /** @type {Function} Called when the error for the field changes */
    onValidationChange: T.func,
    /** @type {Function} Use to force re-validation */
    triggerValidate$: T.object,
    onChange: T.func,

    disabled: T.bool,
    isEditing: T.bool,
    /* Override value behavior. Display as edit mode but continue to display the props.value */
    disabledInput: T.bool,
    isSaving: T.bool,
    isRequired: T.bool,

    // Required for checkbox type
    trueValue: T.string,
    falseValue: T.string,

    isCustomCheckmark: T.bool,
    customCheckmarkClass: T.string,
    fieldTitleClass: T.string,
    isErrorAfterLabel: T.bool,
  };

  static defaultProps = {
    validationMode: ValidationMode.onChange,
  };

  constructor(props) {
    super(props);
    this.state = {
      uid: uuidv1(),
      value: this.props.value,
      error: null,
    };

    if (props.validateImmediately) {
      this.state.error = FormFieldComponent.getTextError(this.props.value, this.props.constraints);
    }

    this.handleTextBlur = this.handleTextBlur.bind(this);
    this.handleNumberBlur = this.handleNumberBlur.bind(this);
    this.handleTextChange = this.handleTextChange.bind(this);
    this.handleNumberChange = this.handleNumberChange.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isEditing && this.props.isEditing) {
      this.setState({
        value: prevProps.value,
        error: null,
      });

    } else if (prevProps.isEditing && !this.props.isEditing) {
      this.setState({
        value: null,
        error: null,
      });

    } else if (prevProps.isEditing && prevProps.value !== this.props.value && this.state.value === prevProps.value) {
      this.setState({
        value: this.props.value,
        error: FormFieldComponent.getTextError(this.props.value, this.props.constraints),
      });

    } else if (prevProps.constraints !== this.props.constraints
      && (prevProps.validateImmediately || this.state.error)
    ) {
      let error;
      switch (this.props.type) {
        case INPUT_TYPES.text:
        case INPUT_TYPES.password:
        case INPUT_TYPES.email:
        case INPUT_TYPES.text_long:
          error = this.validateText(this.props.value, this.state.value);
          break;

        case INPUT_TYPES.number:
          error = this.validateNumber(this.props.value, this.state.value);
          break;
      }

      if (error !== this.state.error) {
        this.handleOnValidationChangeDispatch(this.getValue(), error);

        this.setState({error});
      }
    }

    if (this.props.triggerValidate$ && !this.triggerValidateSubscription) {
      this.triggerValidateSubscription = this.props.triggerValidate$
        .throttleTime(500, void 0, {leading: true, trailing: true})
        .subscribe(() => {
          const method = this.getBlurMethod();
          method();
        });
    }
  }

  componentWillUnmount() {
    if (this.triggerValidateSubscription) {
      this.triggerValidateSubscription.unsubscribe();
      this.triggerValidateSubscription = null;
    }
  }

  handleTextBlur() {
    const error = this.validateText(this.props.value, this.state.value);

    if (error !== this.state.error) {
      this.handleOnValidationChangeDispatch(this.getValue(), error);

      this.setState({error});
    }
  }

  handleTextChange(event) {
    const prevValue = this.props.value;
    const nextValue = event.target.value;
    let nextState;
    if (this.props.validationMode === ValidationMode.onChange) {
      const error = this.validateText(prevValue, nextValue);
      let customValidation;
      if (isFunction(this.props.onValidate) && (customValidation = this.props.onValidate({
        prev: prevValue,
        next: nextValue,
      }))) {
        nextState = {
          value: customValidation.value,
          error: customValidation.error,
        };

      } else if (this.props.constraints
        && isNumber(this.props.constraints.max)
        && isString(nextValue)
        && nextValue.length > this.props.constraints.max
      ) {
        // ignore the error and just force the allowed max length
        nextState = {
          value: nextValue.substr(0, this.props.constraints.max),
          error,
        };
      } else {
        nextState = {
          value: nextValue,
          error,
        };
      }

      if (nextState.error !== this.state.error) {
        this.handleOnValidationChangeDispatch(this.getValue(), nextState.error);
      }
    } else {
      nextState = {
        value: nextValue,
      };
    }

    this.handleOnChangeDispatch(nextState);

    this.setState(nextState);
  }

  validateText(prevValue, nextValue) {
    const error = FormFieldComponent.getTextError(nextValue, this.props.constraints);
    let customValidation;
    if (isFunction(this.props.onValidate) && (customValidation = this.props.onValidate({
      prev: prevValue,
      next: nextValue,
    }))) {
      return customValidation.error;
    } else if (this.props.constraints
      && isNumber(this.props.constraints.max)
      && isString(nextValue)
      && nextValue.length > this.props.constraints.max
    ) {
      // ignore the error and just force the allowed max length
      return null;

    } else if (error) {
      return error;

    } else {
      return null;
    }
  }

  handleNumberBlur() {
    const error = this.validateNumber(this.props.value, this.state.value);

    if (error !== this.state.error) {
      this.handleOnValidationChangeDispatch(this.getValue(), error);

      this.setState({error});
    }
  }

  handleNumberChange(event) {
    const parser = get(this.props.constraints, 'isDecimal') ? parseFloatEvent : parseNumberEvent;

    const prevValue = this.props.value;
    const nextValue = parser(event);
    const error = this.validateNumber(prevValue, nextValue);
    const nextIsNumber = isNumber(nextValue);
    let nextState;
    let customValidation;
    if (this.props.validationMode === ValidationMode.onChange) {
      if (isFunction(this.props.onValidate) && (customValidation = this.props.onValidate({
        prev: prevValue,
        next: nextValue,
      }))) {
        nextState = {
          value: customValidation.value,
          error: customValidation.error,
        };
      } else if (nextIsNumber) {
        nextState = {
          value: applyNumericConstraint(nextValue, this.props.constraints),
          error,
        };
      } else if (!nextValue && (!this.props.constraints || this.props.constraints.nullable)) {
        nextState = {
          value: null,
          error,
        };
      }

      if (nextState && nextState.error !== this.state.error) {
        this.handleOnValidationChangeDispatch(this.getValue(), nextState.error);
      }
    } else {
      nextState = {
        value: nextValue,
      };
    }

    if (nextState) {
      this.handleOnChangeDispatch(nextState);

      this.setState(nextState);
    }
  }

  validateNumber(prevValue, nextValue) {
    const error = FormFieldComponent.getNumberError(nextValue, this.props.constraints);
    const nextIsNumber = isNumber(nextValue);
    let customValidation;
    if (isFunction(this.props.onValidate) && (customValidation = this.props.onValidate({
      prev: prevValue,
      next: nextValue,
    }))) {
      return customValidation.error;

    } else if (this.props.constraints
      && isNumber(this.props.constraints.max)
      && nextIsNumber
      && nextValue > this.props.constraints.max
    ) {
      // ignore the error and just force the allowed max
      return null;

    } else if (this.props.constraints
      && isNumber(this.props.constraints.min)
      && nextIsNumber
      && nextValue < this.props.constraints.min
    ) {
      // ignore the error and just force the allowed min
      return null;

    } else if (error) {
      return error;

    } else {
      return null;
    }
  }

  handleCheckboxChange() {
    const nextState = {
      value: !this.state.value,
    };

    if (this.props.onChange) {
      this.props.onChange({
        target: {
          value: nextState.value,
          isValid: true,
        },
      });
    }

    this.setState(nextState);
  }

  handleOnValidationChangeDispatch(value, error) {
    if (isFunction(this.props.onValidationChange)) {
      this.props.onValidationChange({
        target: {
          value,
          isValid: !error,
        },
      });
    }
  }

  handleOnChangeDispatch(nextState) {
    if (isFunction(this.props.onChange)) {
      const target = {
        value: nextState.value,
      };

      if (this.props.validationMode === ValidationMode.onChange) {
        target.isValid = !nextState.error;
      } else {
        target.isValid = !this.state.error;
      }

      this.props.onChange({
        target,
      });
    }
  }

  render() {
    return (
      <FormFieldView
        className={this.props.className}
        uid={this.state.uid}
        type={this.props.type}
        validationMode={this.props.validationMode}
        label={this.props.label}
        placeholder={this.props.placeholder}
        constraints={this.props.constraints}
        value={this.getValue()}
        error={this.getError()}
        disabled={this.props.disabled || this.props.disabledInput}
        isEditing={this.props.isEditing}
        isSaving={this.props.isSaving}
        isRequired={this.props.isRequired}
        isCustomCheckmark={this.props.isCustomCheckmark}
        customCheckmarkClass={this.props.customCheckmarkClass}
        fieldTitleClass={this.props.fieldTitleClass}
        isErrorAfterLabel={this.props.isErrorAfterLabel}

        trueValue={this.props.trueValue}
        falseValue={this.props.falseValue}

        onChange={this.getHandleChange()}
        onBlur={this.getHandleBlur()}
      />
    );
  }

  getValue() {
    if (this.props.isEditing && !this.props.disabledInput) {
      return this.state.value;
    } else {
      return this.props.value;
    }
  }

  getError() {
    if (!this.props.isEditing) {
      return null;
    } else if (this.props.error) {
      return this.props.error;
    } else if (this.state.error) {
      return this.state.error;
    } else {
      return null;
    }
  }

  getHandleChange() {
    switch (this.props.type) {
      case INPUT_TYPES.text:
      case INPUT_TYPES.password:
      case INPUT_TYPES.email:
      case INPUT_TYPES.text_long:
        return this.handleTextChange;

      case INPUT_TYPES.number:
        return this.handleNumberChange;

      case INPUT_TYPES.checkbox:
        return this.handleCheckboxChange;

    }
  }

  getHandleBlur() {
    if (this.props.validationMode === ValidationMode.onBlur) {
      return this.getBlurMethod();
    }
  }

  getBlurMethod() {
    switch (this.props.type) {
      case INPUT_TYPES.text:
      case INPUT_TYPES.password:
      case INPUT_TYPES.email:
      case INPUT_TYPES.text_long:
        return this.handleTextBlur;

      case INPUT_TYPES.number:
        return this.handleNumberBlur;
    }
  }

  static getTextError(nextValue, constraints) {
    const len = isString(nextValue) ? nextValue.length : 0;
    if (!constraints) {
      return null;

    } else if (len === 0 && !constraints.nullable) {
      return {
        code: 'default.blank.message',
      };

    } else if (isNumber(constraints.min) && len < constraints.min) {
      return {
        code: 'default.invalid.min.string.message',
        params: {
          min: constraints.min,
        },
      };

    } else if (isNumber(constraints.max) && len > constraints.max) {
      return {
        code: 'default.invalid.max.string.message',
        params: {
          max: constraints.max,
        },
      };

    } else {
      return null;
    }
  }

  static getNumberError(nextValue, constraints) {
    if (!constraints) {
      return null;

    } else if (!isNumber(nextValue)) {
      return constraints.nullable ? null : {
        code: 'default.blank.message',
      };

    } else if (isNumber(constraints.min) && nextValue < constraints.min) {
      return {
        code: 'default.invalid.min.message',
        params: {
          min: constraints.min,
        },
      };

    } else if (isNumber(constraints.max) && nextValue > constraints.max) {
      return {
        code: 'default.invalid.max.message',
        params: {
          max: constraints.max,
        },
      };

    } else {
      return null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/components/forms/FormField/FormFieldComponent.js